import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, ShieldCheck, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";
import { MARKETPLACE_PAGES } from "@/data/marketplace-pages";
import { MARKETPLACE_DETAILS } from "@/data/marketplace-details";
import { BLOG_POSTS } from "@/data/blog";
import { serviceSchema as toServiceSchema, faqSchema as toFaqSchema } from "@/data/schema";
import NotFound from "@/pages/NotFound";
import { COMPANY } from "@/data/content";

export default function MarketplaceServicePage() {
  const { slug } = useParams();
  const page = MARKETPLACE_PAGES[slug];
  if (!page) return <NotFound />;
  const details = MARKETPLACE_DETAILS[slug] || {};
  const guide = BLOG_POSTS.find((p) => p.slug === details.guide);

  const num = COMPANY.whatsapp.replace(/\D/g, "");
  const waLink = `https://wa.me/${num}?text=${encodeURIComponent(`Hi Orina, I want help with ${page.marketplace}.`)}`;

  const serviceSchema = toServiceSchema({
    name: page.hero,
    description: page.intro,
    path: `/services/${slug}`,
    serviceType: page.intent,
    areaServed: { "@type": "Country", name: "India" },
  });
  const faqSchema = toFaqSchema(page.faqs);

  return (
    <>
      <SEO
        title={page.hero}
        description={page.tagline}
        keywords={page.intent}
        path={`/services/${slug}`}
        schema={[serviceSchema, faqSchema]}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: page.marketplace, path: `/services/${slug}` }
        ]}
      />

      <section className="border-b-2 border-black bg-[#FFD700] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape-dark">{page.marketplace} Services</div>
          <h1 className="display mt-6 max-w-4xl text-4xl font-black leading-[1.02] tracking-tight md:text-5xl lg:text-6xl">
            {page.hero}
          </h1>
          <p className="mt-5 max-w-3xl text-base text-black/85 md:text-lg">{page.tagline}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact/?type=expert" className="btn-brutal" data-testid={`mp-cta-expert-${slug}`}>
              Talk to a {page.marketplace} expert <ArrowUpRight size={16} />
            </Link>
            <a href={waLink} target="_blank" rel="noreferrer" className="btn-brutal secondary" data-testid={`mp-cta-wa-${slug}`}>
              <MessageCircle size={16} /> WhatsApp Orina
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <div className="eyebrow">Direct answer</div>
          <h2 className="display mt-3 text-2xl font-black md:text-3xl">What we do on {page.marketplace}</h2>
          <p className="mt-4 text-base leading-relaxed text-black/85 md:text-lg">{page.intro}</p>
        </div>
      </section>

      <section className="border-y-2 border-black bg-[#FAFAF7] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">Our {page.marketplace} process</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {page.process.map((p, i) => (
              <div key={p.t} className="card-brutal">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center border-2 border-black bg-black text-sm font-black text-[#FFD700]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <h3 className="display text-lg font-black">{p.t}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-black/75">{p.d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {details.how && (
        <section className="bg-white py-16 md:py-24">
          <div className="mx-auto max-w-4xl px-4 md:px-8">
            <h2 className="display text-3xl font-black md:text-4xl">How selling on {page.marketplace} works</h2>
            {details.how.map((para) => (
              <p key={para.slice(0, 40)} className="mt-4 text-base leading-relaxed text-black/85 md:text-lg">{para}</p>
            ))}
            <h3 className="display mt-10 text-xl font-black md:text-2xl">Who this service is for</h3>
            <p className="mt-3 text-base leading-relaxed text-black/85 md:text-lg">{details.who}</p>
          </div>
        </section>
      )}

      {details.documents && (
        <section className="border-y-2 border-black bg-[#FAFAF7] py-16 md:py-24">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 md:grid-cols-2 md:px-8">
            <div>
              <h2 className="display text-3xl font-black md:text-4xl">Documents to prepare</h2>
              <ul className="mt-6 space-y-3">
                {details.documents.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-base">
                    <CheckCircle2 className="mt-1 shrink-0" size={18} /> {d}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="display text-3xl font-black md:text-4xl">How long it takes</h2>
              <p className="mt-6 text-base leading-relaxed text-black/85 md:text-lg">{details.timeline}</p>
              {details.related && (
                <>
                  <h3 className="display mt-10 text-xl font-black">Related marketplaces</h3>
                  <ul className="mt-4 space-y-2">
                    {details.related.filter((r) => MARKETPLACE_PAGES[r]).map((r) => (
                      <li key={r}>
                        <Link to={`/services/${r}/`} className="font-bold underline decoration-[#FFD700] decoration-2 underline-offset-4 hover:bg-[#FFD700]">
                          {MARKETPLACE_PAGES[r].hero}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">What you get</h2>
          <ul className="mt-6 space-y-3">
            {page.outcomes.map((o) => (
              <li key={o} className="flex items-start gap-3 text-base md:text-lg">
                <CheckCircle2 className="mt-1 shrink-0 text-[#25D366]" size={20} /> {o}
              </li>
            ))}
          </ul>

          <div className="mt-10 grid gap-4 border-2 border-black bg-[#FFD700] p-6 md:grid-cols-3">
            <Link to="/tools/marketplace-calculators/" className="text-sm font-bold uppercase tracking-widest underline hover:no-underline">Model your {page.marketplace} profit →</Link>
            <Link to="/tools/listing-score/" className="text-sm font-bold uppercase tracking-widest underline hover:no-underline">Score your listing →</Link>
            <Link to={guide ? `/blog/${guide.slug}/` : "/blog/"} className="text-sm font-bold uppercase tracking-widest underline hover:no-underline">Read the {page.marketplace} guide →</Link>
          </div>
        </div>
      </section>

      <section className="border-y-2 border-black bg-[#FAFAF7] py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">FAQ — {page.marketplace}</h2>
          <div className="mt-8 space-y-4">
            {page.faqs.map((f) => (
              <details key={f.q} className="card-brutal">
                <summary className="cursor-pointer text-base font-bold md:text-lg">{f.q}</summary>
                <p className="mt-3 text-sm text-black/80 md:text-base">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        title={`Ready to grow on ${page.marketplace}?`}
        subtitle={`Free 30-minute audit. We'll show you exactly what to fix on ${page.marketplace} — with numbers.`}
      />
    </>
  );
}
